import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Play } from 'lucide-react';
import clsx from 'clsx';
import { formatDuration } from './formatDuration';
import { useHlsPlayer } from '@/hooks/useHlsPlayer';
import { useFailedImages } from '@/hooks/useFailedImages';
import { VideoControlBar } from '@/components/video/controls/VideoControlBar';
import { useVideoControls } from '@/components/video/controls/useVideoControls';
import type { Fragment } from '@/hooks/useSessionWatchData';

export interface WatchPlayerProps {
  fragment: Fragment;
  /** e.g. "Part 1/3"; shown as a chip over the placeholder when set. */
  partLabel?: string | null;
  onEnded?: () => void;
  /**
   * Fired at most once per activation when the playlist fails to load with a
   * fatal, non-"unsupported" error. `useHlsPlayer` folds every fatal hls.js/
   * native error into a single translated string with no HTTP status, so we
   * can't tell a token-expiry 403 apart from a transient network blip here.
   * The parent (WatchPage) treats this as "refetch the recording and retry",
   * which is a harmless no-op if the failure wasn't actually auth-related.
   */
  onPlaybackAuthError?: () => void;
  className?: string;
  /**
   * Skips the placeholder and attaches the playlist immediately. Used when
   * the parent switches fragments after an angle switch or fragment
   * auto-advance — the play gesture already happened, so re-requiring a
   * click here would be a regression, not a safety measure.
   */
  autoActivate?: boolean;
  /**
   * One-shot seek target (seconds) applied the first time the current
   * fragment's media becomes playable. Clamped to [0, fragment duration].
   * Re-applied when either the fragment or the value changes (e.g. the ?t=
   * param is edited, or ?r= points at a different fragment); NOT re-applied
   * on the recordings-poll token re-mint or on fragment auto-advance.
   */
  initialTime?: number | null;
  /**
   * Lifts the live media currentTime to the parent so the Share popover's
   * "Start at" clock can tick while playing and freeze when paused.
   * (timeupdate fires only while playing / seeking.)
   */
  onTimeUpdate?: (seconds: number) => void;
  /**
   * Fired at most once per fragment when playback actually starts — i.e.
   * when the playlist is attached (the placeholder's play click, or an
   * autoActivate carried by an angle switch / fragment auto-advance).
   * Parents use this to count views (issue #649); dedup lives server-side,
   * so repeated fires for the same fragment are harmless.
   */
  onPlaybackStart?: (fragmentId: string) => void;
}

/**
 * Lazy-activated HLS player for one angle group's current fragment on the
 * public /watch page. Renders an inert dark placeholder (play button,
 * duration badge, part chip) until the viewer presses play — no playlist URL
 * reaches `useHlsPlayer`/hls.js before then, so segment presigning never
 * happens for a card nobody has clicked. `autoActivate` bypasses that gate
 * for fragment switches that already carry a user gesture.
 *
 * The `<video>` element itself is mounted from the start, source-less and
 * hidden under the placeholder. Element wiring in
 * `useVideoControls`/`useMediaElement` follows element identity changes, so
 * the element may mount late or be replaced freely and its
 * `timeupdate`/`play`/`durationchange` listeners always follow (the old
 * ref-keyed binding froze the seek slider at 0:00 for late-mounted elements
 * — issue #819). A source-less video fetches nothing, so lazy-presign
 * behavior is unaffected.
 */
export function WatchPlayer({
  fragment,
  partLabel,
  onEnded,
  onPlaybackAuthError,
  className,
  autoActivate = false,
  initialTime = null,
  onTimeUpdate,
  onPlaybackStart,
}: WatchPlayerProps) {
  const { t } = useTranslation('pages');
  const videoRef = useRef<HTMLVideoElement>(null);
  // Fullscreen targets this shell rather than the <video> so the control bar
  // stays on top of the picture; see useVideoControls.onToggleFullscreen.
  const containerRef = useRef<HTMLDivElement>(null);
  // Keyed by fragment id (not a bare boolean) so that switching to a new
  // fragment deactivates synchronously with the same render that sees the
  // new fragment's URL — there is no commit where a stale "activated" flag
  // can pair with a different fragment's playbackUrl. A boolean paired with
  // a reset effect could not guarantee this: React can commit a render
  // where the new fragment's props have already landed but the effect that
  // would reset the flag hasn't run yet, briefly handing the new URL to
  // useHlsPlayer while "activated" is still true from the previous
  // fragment. Deriving activation from an id comparison has no such gap.
  const [activatedFragmentId, setActivatedFragmentId] = useState<string | null>(
    autoActivate ? fragment.id : null,
  );
  const hasFiredAuthErrorRef = useRef(false);

  const isActivated = activatedFragmentId === fragment.id || autoActivate;

  // The URL fed to useHlsPlayer is pinned per activation rather than read
  // live off `fragment.playbackUrl` every render: useSessionWatchData's
  // recordings poll re-mints every playable recording's token on each
  // refresh, so the playbackUrl STRING changes even though nothing else
  // about the fragment does. useHlsPlayer keys its attach effect on that
  // string, so feeding it the live value would tear down and recreate the
  // HLS instance — restarting playback from 0:00 — on every poll tick while
  // a viewer is mid-playback.
  //
  // The pin is recaptured synchronously within this render (never via a
  // follow-up effect, for the same "no gap" reason `isActivated` above is
  // derived from an id comparison rather than a boolean + reset effect) in
  // exactly two cases:
  //  - the pinned id doesn't match the current fragment (first activation,
  //    or a fragment switch) — always recapture; or
  //  - a previous playback failure (see the auth-error effect below)
  //    invalidated the pin AND the fragment now carries a genuinely
  //    different URL than the one pinned — i.e. the parent's refetch
  //    (triggered by onPlaybackAuthError) has actually landed a fresh
  //    token. Comparing the URL value, rather than reacting to the
  //    invalidation flag alone, matters: the invalidating effect fires
  //    before the parent's refetch resolves, so the very next render after
  //    it still carries the same stale URL. Recapturing right then would
  //    just re-pin the broken value and permanently miss the real refresh
  //    once it lands, since nothing would flip the flag back on afterwards.
  const pinnedUrlRef = useRef<{ id: string; url: string | null } | null>(null);
  const pinInvalidatedRef = useRef(false);

  if (isActivated) {
    const pinned = pinnedUrlRef.current;
    if (!pinned || pinned.id !== fragment.id) {
      pinnedUrlRef.current = { id: fragment.id, url: fragment.playbackUrl };
      pinInvalidatedRef.current = false;
    } else if (pinInvalidatedRef.current && fragment.playbackUrl !== pinned.url) {
      pinnedUrlRef.current = { id: fragment.id, url: fragment.playbackUrl };
      pinInvalidatedRef.current = false;
    }
  }

  const sourceUrl = isActivated ? pinnedUrlRef.current?.url ?? null : null;
  const {
    engine,
    status,
    levels,
    activeHeight,
    isAutoQuality,
    selectQuality,
  } = useHlsPlayer(videoRef, sourceUrl);
  // A partially-encoded recording is served as an HLS EVENT playlist, which
  // hls.js treats as live and reports as `Infinity` duration — leaving the
  // seek bar disabled. Feed the fragment's metadata duration as a floor so
  // scrubbing stays usable, mirroring SynchronizedVideoPlayer. See
  // recordingPlaybackService.buildMediaPlaylist (EVENT vs VOD).
  const { binding, containerProps } = useVideoControls(videoRef, containerRef, {
    fallbackDuration: fragment.mediaDuration ?? fragment.duration,
  });
  const isUnsupported = engine === 'unsupported';
  const isPlaybackUnavailable = isActivated && !fragment.playbackUrl;
  const showControlBar =
    isActivated && !isUnsupported && !isPlaybackUnavailable;

  // Playlist-level failures (not the terminal "browser can't play HLS at
  // all" case, which the message below already communicates) fire the
  // parent's refetch-and-retry hook. Guarded by a ref so re-renders while an
  // error is still active — including from an unmemoized callback prop —
  // don't repeat the call; the guard clears once status leaves "error".
  useEffect(() => {
    if (status === 'error' && !isUnsupported) {
      if (!hasFiredAuthErrorRef.current) {
        hasFiredAuthErrorRef.current = true;
        // Mark the pin stale so that once the parent's refetch (triggered
        // below) lands a genuinely different URL for this same fragment,
        // the render-time check above adopts it instead of continuing to
        // feed useHlsPlayer the URL that just failed.
        pinInvalidatedRef.current = true;
        onPlaybackAuthError?.();
      }
    } else {
      hasFiredAuthErrorRef.current = false;
    }
  }, [status, isUnsupported, onPlaybackAuthError]);

  const appliedInitialTimeRef = useRef<{ id: string; time: number } | null>(null);

  // One-shot seek to a ?t= deep-link offset. Fires the first time the
  // activated media reports ready, clamped to the fragment's metadata
  // duration (matches YouTube: an oversized ?t starts at the end rather
  // than being dropped). The guard is keyed on {fragment.id, initialTime}:
  // it re-fires whenever EITHER the fragment or the ?t= value changes, and
  // skips only when BOTH are unchanged. That "both unchanged" case is what
  // covers the recordings-poll token re-mint — the poll re-mints every
  // playable recording's playbackUrl string while neither the fragment id
  // nor ?t= moves, so a viewer scrubbing mid-playback isn't yanked back to
  // the deep-link offset on every poll tick.
  useEffect(() => {
    if (!isActivated || isPlaybackUnavailable || initialTime == null) {
      return;
    }
    if (status !== 'ready') {
      return;
    }
    if (
      appliedInitialTimeRef.current?.id === fragment.id &&
      appliedInitialTimeRef.current.time === initialTime
    ) {
      return;
    }
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const maxDuration = fragment.mediaDuration ?? fragment.duration;
    const upper =
      maxDuration != null && Number.isFinite(maxDuration) && maxDuration > 0
        ? maxDuration
        : Infinity;
    video.currentTime = Math.max(0, Math.min(initialTime, upper));
    appliedInitialTimeRef.current = { id: fragment.id, time: initialTime };
    onTimeUpdate?.(video.currentTime);
  }, [
    isActivated,
    isPlaybackUnavailable,
    initialTime,
    status,
    fragment.id,
    fragment.mediaDuration,
    fragment.duration,
    onTimeUpdate,
  ]);

  // Lift the live currentTime so the Share popover's "Start at" clock can
  // tick while playing and freeze when paused (timeupdate only fires while
  // playing / seeking). Attached only once the <video> exists (isActivated).
  useEffect(() => {
    if (!onTimeUpdate || !isActivated) {
      return;
    }
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const handleTimeUpdate = () => onTimeUpdate(video.currentTime);
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [onTimeUpdate, isActivated]);

  const handleActivate = useCallback(() => {
    setActivatedFragmentId(fragment.id);
  }, [fragment.id]);

  // One playback-start notification per fragment: fires the first time the
  // fragment's playlist is attached (real activation, and its URL has
  // landed). Keyed by fragment id so a poll-driven re-render — or a later
  // switch back to an already-counted fragment — never double-fires.
  const firedPlaybackStartRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isActivated || isPlaybackUnavailable) {
      return;
    }
    if (firedPlaybackStartRef.current === fragment.id) {
      return;
    }
    firedPlaybackStartRef.current = fragment.id;
    onPlaybackStart?.(fragment.id);
  }, [isActivated, isPlaybackUnavailable, fragment.id, onPlaybackStart]);

  const handleEnded = useCallback(() => {
    onEnded?.();
  }, [onEnded]);

  const { hasFailed, markFailed } = useFailedImages();
  const previewUrl =
    fragment.thumbnailUrl && !hasFailed(fragment.thumbnailUrl)
      ? fragment.thumbnailUrl
      : undefined;

  const duration = formatDuration(fragment.mediaDuration ?? fragment.duration);

  return (
    <div
      ref={containerRef}
      {...(showControlBar ? containerProps : {})}
      className={clsx(
        'video-player-shell group/player relative aspect-video w-full overflow-hidden rounded-2xl bg-black',
        // The shell owns keyboard shortcuts once controls are live, so it
        // takes focus; suppress the default ring in favour of a subtler one.
        showControlBar && 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#60a5fa]',
        className,
      )}
    >
      {/* Always mounted — see the component docblock. Without a source it is
          inert and invisible under the placeholder/error overlays. */}
      <video
        ref={videoRef}
        data-testid="watch-player-video"
        poster={previewUrl}
        className="h-full w-full bg-black object-contain"
        // No `controls`: VideoControlBar replaces the native bar so that
        // quality and speed can live alongside transport. `playsInline`
        // still matters — without it iOS hijacks playback into its own
        // fullscreen player and this bar would never be seen.
        playsInline
        autoPlay
        onClick={binding.onPlayPause}
        onEnded={handleEnded}
      />

      {isPlaybackUnavailable && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black px-6 text-center text-slate-300">
          <AlertTriangle className="h-7 w-7 text-amber-400" aria-hidden="true" />
          <p className="text-sm font-medium">{t('watch.playbackError')}</p>
        </div>
      )}

      {!isActivated && !isPlaybackUnavailable && (
        <button
          type="button"
          onClick={handleActivate}
          aria-label={t('watch.play')}
          className="group absolute inset-0 flex h-full w-full items-center justify-center border-none bg-gradient-to-br from-slate-800 via-slate-900 to-black"
        >
          {previewUrl && (
            <img
              src={previewUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              onError={() => markFailed(previewUrl)}
            />
          )}
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-secondary-900 shadow-lg transition-transform group-hover:scale-105">
            <Play className="ml-1 h-7 w-7" fill="currentColor" aria-hidden="true" />
          </span>
        </button>
      )}

      {!isActivated && duration && (
        <div
          data-testid="watch-player-duration"
          className="pointer-events-none absolute bottom-3 right-3 rounded-md bg-black/75 px-2 py-1 text-xs font-medium tabular-nums text-slate-200"
        >
          {duration}
        </div>
      )}

      {!isActivated && partLabel && (
        <div className="pointer-events-none absolute left-3 top-3 rounded-md bg-black/75 px-2 py-1 text-xs font-medium text-slate-200">
          {partLabel}
        </div>
      )}

      {showControlBar && (
        <VideoControlBar
          {...binding}
          quality={{
            levels,
            activeHeight,
            isAuto: isAutoQuality,
            isLoading: status === 'loading',
          }}
          disabled={status !== 'ready'}
          onQualityChange={selectQuality}
        />
      )}

      {isActivated && isUnsupported && !isPlaybackUnavailable && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black px-6 text-center text-slate-300">
          <AlertTriangle className="h-7 w-7 text-amber-400" aria-hidden="true" />
          <p className="text-sm font-medium">{t('watch.hlsNotSupported')}</p>
        </div>
      )}
    </div>
  );
}

export default WatchPlayer;
